import {ReactNode, useEffect, useState} from 'react';
import {useRevealOnView} from './useRevealOnView';
import styles from './Step11Viz.module.css';

export type Step11Step = {
  title: string;
  short: string;
  detail: string;
};

export type Step11Brand = {
  rxName: string;
  rxDosage: string;
  pharmacy: string;
  deliveryLabel: string;
};

const CYCLE_MS = 3800;

/* ─── Mini widgets ─────────────────────────────────────────────────────
 * Each widget gets a distinct accent palette per Nyx so the carousel feels
 * like a journey: green checks (intake) → gold credentials (review) →
 * green APPROVED stamp (Rx) → purple-to-teal delivery track (package) →
 * purple + amber chat bubbles (support).
 */

const CheckGlyph = ({size = 10}: {size?: number}) => (
  <svg width={size} height={size} viewBox="0 0 9 9" aria-hidden="true">
    <path
      d="M1.5 4.5 L3.5 6.5 L7 2.5"
      stroke="var(--osa-accent-healthy)"
      strokeWidth="1.7"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const INTAKE_ROWS = [
  {label: 'Symptoms', delayMs: 150},
  {label: 'Medical history', delayMs: 350},
  {label: 'Sleep patterns', delayMs: 550},
];

const MiniIntake = () => (
  <div className={styles.miniPanel}>
    <span className={styles.miniEyebrow}>Medical intake</span>
    {INTAKE_ROWS.map((row, i) => (
      <div
        key={row.label}
        className={`${styles.intakeRow} ${i < INTAKE_ROWS.length - 1 ? styles.intakeRowBorder : ''}`}
      >
        <span className={styles.checkBox} style={{animationDelay: `${row.delayMs}ms`}} aria-hidden="true">
          <CheckGlyph />
        </span>
        <span className={styles.intakeRowLabel} style={{animationDelay: `${row.delayMs + 50}ms`}}>
          {row.label}
        </span>
        <span className={styles.intakeComplete} style={{animationDelay: `${row.delayMs + 150}ms`}}>
          COMPLETE
        </span>
      </div>
    ))}
  </div>
);

const DOC_LINE_WIDTHS = [78, 92, 60, 86];

const MiniReview = () => (
  <div className={styles.miniPanel}>
    <div className={styles.reviewHeader}>
      <span className={styles.reviewAvatar} aria-hidden="true">
        <span className={styles.reviewAvatarText}>HG</span>
      </span>
      <div className={styles.reviewIdentity}>
        <span className={styles.reviewName}>Dr. Hunter Greene, MD</span>
        <span className={styles.reviewLicense}>Sleep medicine · Licensed in CA, NY, TX</span>
      </div>
    </div>

    <div className={styles.reviewDocs}>
      {DOC_LINE_WIDTHS.map((w, i) => (
        <span
          key={i}
          className={`${styles.docLine} ${i === 2 ? styles.docLineHighlight : ''}`}
          style={{width: `${w}%`, animationDelay: `${200 + i * 60}ms`}}
        />
      ))}
    </div>
  </div>
);

const MiniStamp = ({rxName, rxDosage}: {rxName: string; rxDosage: string}) => (
  <div className={`${styles.miniPanel} ${styles.stampPanel}`}>
    <span className={styles.miniEyebrow}>Prescription · Rx</span>
    <span className={styles.stampRxName}>{rxName}</span>
    <span className={styles.stampRxDosage}>{rxDosage}</span>
    <span className={styles.stampDivider} aria-hidden="true" />
    <span className={styles.stampDoctor}>Hunter Greene, MD · CA #G123456</span>

    <span className={styles.stampApproved} aria-hidden="true">
      <span className={styles.stampApprovedText}>APPROVED</span>
    </span>
  </div>
);

const PackageGlyph = ({size = 36}: {size?: number}) => (
  <span className={styles.packageGlyph} style={{width: size, height: size}}>
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M2 5 L8 2 L14 5 V12 L8 15 L2 12 Z"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path d="M2 5 L8 8 L14 5 M8 8 V15" stroke="#ffffff" strokeWidth="1.3" strokeLinejoin="round" fill="none" />
    </svg>
  </span>
);

const MiniPackage = ({pharmacy, deliveryLabel}: {pharmacy: string; deliveryLabel: string}) => (
  <div className={styles.miniPanel}>
    <div className={styles.packageHeader}>
      <PackageGlyph size={36} />
      <div className={styles.packageInfo}>
        <div className={styles.packagePharmacyRow}>
          <span className={styles.packagePharmacy}>{pharmacy}</span>
          <span className={styles.packageLabelPill}>{deliveryLabel}</span>
        </div>
        <span className={styles.packageTracked}>Tracked end-to-end</span>
      </div>
    </div>

    <div className={styles.packageTrack}>
      <span className={styles.packageTrackFill} aria-hidden="true" />
    </div>
  </div>
);

const MiniChat = () => (
  <div className={styles.chatRoot}>
    <div className={`${styles.chatRow} ${styles.chatRowEnd}`}>
      <div className={`${styles.bubble} ${styles.bubbleUser}`}>Should I take this with food?</div>
    </div>
    <div className={`${styles.chatRow} ${styles.chatRowStart}`}>
      <div className={`${styles.bubble} ${styles.bubbleDoctor}`}>Optional — easier on the stomach the first week.</div>
    </div>
  </div>
);

const MiniWidget = ({index, brand}: {index: number; brand: Step11Brand}) => {
  switch (index) {
    case 0:
      return <MiniIntake />;
    case 1:
      return <MiniReview />;
    case 2:
      return <MiniStamp rxName={brand.rxName} rxDosage={brand.rxDosage} />;
    case 3:
      return <MiniPackage pharmacy={brand.pharmacy} deliveryLabel={brand.deliveryLabel} />;
    case 4:
      return <MiniChat />;
    default:
      return null;
  }
};

/** Three pulsing bars indicating auto-play, sequenced left→right. */
const PlayDot = () => (
  <span className={styles.playDot} aria-hidden="true">
    <span className={styles.playDotBar} style={{animationDelay: '0ms'}} />
    <span className={styles.playDotBar} style={{animationDelay: '180ms'}} />
    <span className={styles.playDotBar} style={{animationDelay: '360ms'}} />
  </span>
);

/** Auto-cycling 5-step carousel showing the clinical journey. Each chip
 *  always shows its step number; the active chip additionally expands to
 *  show the short label and an animated progress fill that sweeps
 *  left→right over the cycle duration. */
export const StepperCarouselViz = ({steps, brand}: {steps: Step11Step[]; brand: Step11Brand}) => {
  const {ref, triggered} = useRevealOnView();
  const [active, setActive] = useState(0);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!triggered) return;
    const id = setTimeout(() => {
      setActive(prev => (prev + 1) % steps.length);
      setTick(t => t + 1);
    }, CYCLE_MS);
    return () => clearTimeout(id);
  }, [active, tick, steps.length, triggered]);

  const handleTap = (i: number) => {
    if (i === active) {
      setTick(t => t + 1);
      return;
    }
    setActive(i);
    setTick(t => t + 1);
  };

  const step = steps[active];

  return (
    <div ref={ref} className={styles.carouselRoot}>
      <div className={styles.chipRow}>
        {steps.map((s, i) => {
          const isActive = i === active;
          return (
            <button
              key={s.short}
              type="button"
              className={`${styles.chip} ${isActive ? styles.chipActive : ''}`}
              onClick={() => handleTap(i)}
              aria-label={`Step ${i + 1}: ${s.short}`}
            >
              {isActive ? <span key={`prog-${i}-${tick}`} className={styles.chipProgress} aria-hidden="true" /> : null}
              <span className={`${styles.chipNumber} ${isActive ? styles.chipNumberActive : ''}`}>{i + 1}</span>
              {isActive ? (
                <span key={`lbl-${i}-${tick}`} className={styles.chipLabel}>
                  {s.short}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className={styles.contentPanel}>
        <div key={active} className={styles.contentSlot}>
          <h4 className={styles.stepTitle}>{step.title}</h4>
          <div className={styles.miniStage}>
            <MiniWidget index={active} brand={brand} />
          </div>
        </div>
        <div className={styles.playDotWrap}>
          <PlayDot />
        </div>
      </div>

      <div className={styles.copy}>
        <span className={styles.stepNum}>
          Step {active + 1} of {steps.length}
        </span>
        <p className={styles.stepDetail}>{step.detail}</p>
      </div>
    </div>
  );
};

/** Final numbered list of all 5 steps with detail. */
export const StaticStepList = ({items, title}: {items: Step11Step[]; title?: ReactNode}) => (
  <div className={styles.staticList}>
    {title ? <div className={styles.staticHeading}>{title}</div> : null}
    <ol className={styles.staticItems}>
      {items.map((item, i) => (
        <li key={item.short} className={styles.staticItem}>
          <span className={styles.staticIndex}>{i + 1}</span>
          <div className={styles.staticBody}>
            <span className={styles.staticTitle}>{item.title}</span>
            <span className={styles.staticDetail}>{item.detail}</span>
          </div>
        </li>
      ))}
    </ol>
  </div>
);
